/**
 * Newsletter schedule math — client-side mirror of
 * functions/CRM/marketing/newsletterSchedule.js (which itself reuses the
 * Intl helpers from the automation send-window module).
 *
 * Keep the two implementations in lockstep: the "Next sends" preview in the
 * editor must show exactly what processNewsletterSchedules will do. The
 * backend copy is unit-tested in functions/__tests__/newsletterSchedule.test.js.
 *
 * All math is DST-aware via the built-in Intl API; no date libraries needed.
 */

export const DEFAULT_TIMEZONE = 'America/New_York';

const DAY_MS = 24 * 60 * 60 * 1000;

function getOffsetMs(date, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = {};
  for (const p of dtf.formatToParts(date)) parts[p.type] = p.value;
  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0;
  const asUtc = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    hour,
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  return asUtc - date.getTime();
}

function getLocalParts(date, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const parts = {};
  for (const p of dtf.formatToParts(date)) parts[p.type] = p.value;
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: parseInt(parts.year, 10),
    month: parseInt(parts.month, 10),
    day: parseInt(parts.day, 10),
    weekday: weekdayMap[parts.weekday],
  };
}

function zonedWallClockToUtc(year, month1, day, hour, minute, timeZone) {
  const guess = Date.UTC(year, month1 - 1, day, hour, minute, 0);
  const offset1 = getOffsetMs(new Date(guess), timeZone);
  let utc = guess - offset1;
  const offset2 = getOffsetMs(new Date(utc), timeZone);
  if (offset2 !== offset1) utc = guess - offset2;
  return new Date(utc);
}

function intOr(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isInteger(n)) return fallback;
  if (n < min || n > max) return fallback;
  return n;
}

// dayOfMonth clamps (31 → 28 "end of month") instead of falling back, matching backend.
function clampDayOfMonth(value) {
  const n = Number(value);
  if (!Number.isInteger(n)) return 1;
  return Math.min(Math.max(n, 1), 28);
}

function nextWeekdayOccurrence(from, weekday, hour, minute, timeZone) {
  const parts = getLocalParts(from, timeZone);
  const cursor = Date.UTC(parts.year, parts.month - 1, parts.day);
  for (let i = 0; i <= 8; i++) {
    const d = new Date(cursor + i * DAY_MS);
    if (d.getUTCDay() !== weekday) continue;
    const instant = zonedWallClockToUtc(
      d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(),
      hour, minute, timeZone
    );
    if (instant.getTime() > from.getTime()) return instant;
  }
  return null;
}

function nextMonthlyOccurrence(from, dayOfMonth, hour, minute, timeZone) {
  const dom = Math.min(Math.max(dayOfMonth, 1), 28);
  const parts = getLocalParts(from, timeZone);
  for (let i = 0; i <= 2; i++) {
    let year = parts.year;
    let month = parts.month + i;
    while (month > 12) { month -= 12; year += 1; }
    const instant = zonedWallClockToUtc(year, month, dom, hour, minute, timeZone);
    if (instant.getTime() > from.getTime()) return instant;
  }
  return null;
}

/** Next send instant strictly after `from`, or null when nothing is scheduled. */
export function computeNextRunAt(scheduleConfig, from = new Date()) {
  const sc = scheduleConfig || {};

  if (sc.mode === 'one_off') {
    if (!sc.sendAt) return null;
    const at = new Date(sc.sendAt);
    if (Number.isNaN(at.getTime())) return null;
    return at.getTime() > from.getTime() ? at : null;
  }

  if (sc.mode !== 'recurring') return null;

  const tz = sc.timezone || DEFAULT_TIMEZONE;
  const hour = intOr(sc.hour, 9, 0, 23);
  const minute = intOr(sc.minute, 0, 0, 59);

  switch (sc.frequency) {
    case 'weekly':
    case 'biweekly': {
      const weekday = intOr(sc.weekday, 2, 0, 6);
      return nextWeekdayOccurrence(from, weekday, hour, minute, tz);
    }
    case 'monthly': {
      const dom = clampDayOfMonth(sc.dayOfMonth);
      return nextMonthlyOccurrence(from, dom, hour, minute, tz);
    }
    default:
      return null;
  }
}

/** Next send instant after a run happened at `lastRunAt` (biweekly skips one weekly step). */
export function advanceAfterRun(scheduleConfig, lastRunAt) {
  const sc = scheduleConfig || {};
  if (sc.mode !== 'recurring') return null;
  const first = computeNextRunAt(sc, lastRunAt);
  if (!first) return null;
  if (sc.frequency === 'biweekly') {
    return computeNextRunAt(sc, first);
  }
  return first;
}

/** Preview of the next `count` occurrences (for the editor's schedule step). */
export function upcomingOccurrences(scheduleConfig, count = 3, from = new Date()) {
  const out = [];
  let cursor = from;
  for (let i = 0; i < count; i++) {
    const next = i === 0
      ? computeNextRunAt(scheduleConfig, cursor)
      : advanceAfterRun(scheduleConfig, cursor);
    if (!next) break;
    out.push(next);
    cursor = next;
  }
  return out;
}

export const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

export const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

/** Human summary, e.g. "Monthly on the 1st at 9:00 AM ET" / "Weekly on Tuesdays at 8:30 AM ET". */
export function describeSchedule(scheduleConfig) {
  const sc = scheduleConfig || {};
  if (sc.mode === 'one_off') {
    if (!sc.sendAt) return 'One-time send (no date set)';
    const at = new Date(sc.sendAt);
    return `Once on ${at.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: sc.timezone || DEFAULT_TIMEZONE,
    })} ET`;
  }
  if (sc.mode !== 'recurring') return 'Not scheduled';

  const hour = intOr(sc.hour, 9, 0, 23);
  const minute = intOr(sc.minute, 0, 0, 59);
  const h12 = ((hour + 11) % 12) + 1;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const time = `${h12}:${String(minute).padStart(2, '0')} ${ampm} ET`;

  if (sc.frequency === 'monthly') {
    const dom = clampDayOfMonth(sc.dayOfMonth);
    const suffix = dom === 1 || dom === 21 ? 'st' : dom === 2 || dom === 22 ? 'nd' : dom === 3 || dom === 23 ? 'rd' : 'th';
    return `Monthly on the ${dom}${suffix} at ${time}`;
  }
  const weekday = intOr(sc.weekday, 2, 0, 6);
  const dayName = (WEEKDAY_OPTIONS.find((w) => w.value === weekday) || {}).label || 'Tuesday';
  if (sc.frequency === 'biweekly') return `Every 2 weeks on ${dayName}s at ${time}`;
  return `Weekly on ${dayName}s at ${time}`;
}
